import React, { useState, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";

function not(a, bIds) {
  return a.filter((aItem) => !bIds.includes(aItem.id));
}

function intersection(checkedIds, items) {
  return items.filter((item) => checkedIds.includes(item.id));
}

export default function TransferList({
  items,
  setAllProducts,
  selectedItems,
  setSelectedProducts,
  maxProducts = 4,
}) {
  const [checked, setChecked] = useState([]);
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");

  const leftChecked = intersection(checked, items);
  const rightChecked = intersection(checked, selectedItems);

  // Filtrar produtos baseado na pesquisa
  const filteredLeftItems = useMemo(() => {
    if (!leftSearch.trim()) return items;
    return items.filter((item) =>
      item.name.toLowerCase().includes(leftSearch.toLowerCase())
    );
  }, [items, leftSearch]);

  const filteredRightItems = useMemo(() => {
    if (!rightSearch.trim()) return selectedItems;
    return selectedItems.filter((item) =>
      item.name.toLowerCase().includes(rightSearch.toLowerCase())
    );
  }, [selectedItems, rightSearch]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.findIndex(
      (checkedId) => checkedId === value.id
    );
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value.id);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    // Adicione os itens selecionados aos produtos selecionados
    const newSelectedItems = [...selectedItems, ...leftChecked];
    // Limitar o número total de itens selecionados
    const limitedSelectedItems = newSelectedItems.slice(0, maxProducts);
    // Remova os itens selecionados da lista de todos os produtos
    setAllProducts((prevItems) =>
      not(
        prevItems,
        limitedSelectedItems.map((item) => item.id)
      )
    );
    // Atualize a lista de produtos selecionados
    setSelectedProducts(limitedSelectedItems);
    // Limpa os IDs marcados que foram movidos
    setChecked((prevChecked) =>
      prevChecked.filter(
        (id) => !limitedSelectedItems.find((item) => item.id === id)
      )
    );
  };

  const handleCheckedLeft = () => {
    // Adicione os itens selecionados de volta à lista de todos os produtos
    setAllProducts((prevItems) => [...prevItems, ...rightChecked]);
    // Remova os itens selecionados da lista de produtos selecionados
    setSelectedProducts((prevSelected) =>
      not(
        prevSelected,
        rightChecked.map((item) => item.id)
      )
    );
    // Limpa os IDs marcados que foram movidos
    setChecked((prevChecked) =>
      prevChecked.filter((id) => !rightChecked.find((item) => item.id === id))
    );
  };

  const customList = (listItems, isLeftList = true) => {
    const filteredItems = isLeftList ? filteredLeftItems : filteredRightItems;
    const searchValue = isLeftList ? leftSearch : rightSearch;
    const setSearchValue = isLeftList ? setLeftSearch : setRightSearch;

    return (
      <div className="w-80">
        {/* Campo de pesquisa */}
        <div className="relative mb-3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={`Pesquisar ${
              isLeftList ? "produtos" : "selecionados"
            }...`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        {/* Lista de itens */}
        <div className="h-72 overflow-auto border rounded-md bg-white">
          {filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              {searchValue
                ? "Nenhum produto encontrado"
                : "Nenhum produto disponível"}
            </div>
          ) : (
            filteredItems.map((item) => {
              // Desabilita se já atingiu o limite de itens selecionados na lista da esquerda e o item não está selecionado
              const isDisabled =
                (isLeftList &&
                  selectedItems.length >= maxProducts &&
                  !checked.includes(item.id)) ||
                checked.length >= maxProducts;
              return (
                <div
                  key={item.id}
                  className={`flex items-center p-3 rounded-md my-1 mx-2 ${
                    checked.includes(item.id)
                      ? "bg-blue-100 border-blue-300"
                      : "bg-gray-50 hover:bg-gray-100"
                  } ${
                    isDisabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } transition-colors duration-200`}
                  onClick={() => {
                    if (!isDisabled) {
                      handleToggle(item)();
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={checked.includes(item.id)}
                    readOnly
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate block">
                      {item.name}
                    </span>
                    {item.price && (
                      <span className="text-xs text-gray-500">
                        R$ {parseFloat(item.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Contador de resultados */}
        {searchValue && (
          <div className="mt-2 text-xs text-gray-500 text-center">
            {filteredItems.length} de {listItems.length} produtos
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex justify-center items-start space-x-6">
      <div className="flex flex-col items-center">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          Produtos Disponíveis
        </h3>
        {customList(items, true)}
      </div>

      {/* Botões de transferência */}
      <div className="flex flex-col items-center justify-center space-y-4 mt-8">
        <button
          className={`p-2 rounded-full border-2 transition-all duration-200 ${
            leftChecked.length === 0 || selectedItems.length >= maxProducts
              ? "border-gray-200 text-gray-300 cursor-not-allowed"
              : "border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
          }`}
          onClick={handleCheckedRight}
          disabled={
            leftChecked.length === 0 || selectedItems.length >= maxProducts
          }
          title="Adicionar selecionados"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        <button
          className={`p-2 rounded-full border-2 transition-all duration-200 ${
            rightChecked.length === 0
              ? "border-gray-200 text-gray-300 cursor-not-allowed"
              : "border-red-500 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer"
          }`}
          onClick={handleCheckedLeft}
          disabled={rightChecked.length === 0}
          title="Remover selecionados"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      <div className="flex flex-col items-center">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          Produtos Selecionados
        </h3>
        {customList(selectedItems, false)}
      </div>
    </div>
  );
}
